import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetWebInfo,
  CreateOrUpdateWebInfo,
  DeleteWebInfo,
  UploadWebLogo,
  UploadWebFavicon,
  UploadBackgroundImage,
} from "../Api";
import {
  DEFAULT_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const webInfoQueryKeys = {
  webInfo: () => ["webInfo"] as const,
};

export const invalidateWebInfoQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["webInfo"] });
};

export const useWebInfoQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: webInfoQueryKeys.webInfo(),
    queryFn: async () => unwrapApiResponse(await GetWebInfo()),
  });
};

export const useCreateOrUpdateWebInfoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateOrUpdateWebInfo>[0]) =>
      CreateOrUpdateWebInfo(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWebInfoQueries(queryClient);
      }
    },
  });
};

export const useDeleteWebInfoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => DeleteWebInfo(),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWebInfoQueries(queryClient);
      }
    },
  });
};

export const useUploadWebLogoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => UploadWebLogo(file),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWebInfoQueries(queryClient);
      }
    },
  });
};

export const useUploadWebFaviconMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => UploadWebFavicon(file),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWebInfoQueries(queryClient);
      }
    },
  });
};

export const useUploadBackgroundImageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => UploadBackgroundImage(file),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWebInfoQueries(queryClient);
      }
    },
  });
};

export const useWebInfoService = () => {
  const webInfoQuery = useWebInfoQuery();
  const createOrUpdateWebInfoMutation = useCreateOrUpdateWebInfoMutation();
  const deleteWebInfoMutation = useDeleteWebInfoMutation();
  const uploadWebLogoMutation = useUploadWebLogoMutation();
  const uploadWebFaviconMutation = useUploadWebFaviconMutation();
  const uploadBackgroundImageMutation = useUploadBackgroundImageMutation();

  return {
    webInfoQuery,
    createOrUpdateWebInfoMutation,
    deleteWebInfoMutation,
    uploadWebLogoMutation,
    uploadWebFaviconMutation,
    uploadBackgroundImageMutation,
  };
};
